<script setup>
import { ref, computed } from 'vue'
// import NavItem from './NavItem.vue'
import moment from 'moment'

import { Icon } from '@components'

import { useAppointments } from '@store/appointments.js'
import { useSettings } from '@store/settings.js'
import { useCustomizer } from '@store/customizer.js'

const $appointments = useAppointments()
const $settings = useSettings()
const $customizer = useCustomizer()

const showMore = ref(false)

// Added null checks and error handling
const truncatedAbout = computed(() => {
    if (!$settings?.addons) return ''

    let text = $settings.addons.staffs ?
        ($settings.customizer?.sidebar?.business_custom_about || $settings.business?.about) :
        $settings.customizer?.sidebar?.provider_bio

    if (!text) return ''

    if (showMore.value) return text

    // Added trim to handle whitespace
    text = text.trim()

    if (text.length > 76) {
        return text.slice(0, 76) + '...'
    }

    const lines = String(text).split('\n')
    if (lines.length > 3) {
        return lines.slice(0, 3).join('\n') + '...'
    }

    return text
})

const formatTime = (time, format) => {
    try {
        const momentObj = moment(time, format)
        if (!momentObj.isValid()) return ''

        return $settings.customizer?.time?.show_time ?
            momentObj.format('HH:mm') :
            momentObj.format('hh:mm A')
    } catch (error) {
        console.error('Time formatting error:', error)
        return ''
    }
}

const logo = computed(() => {
    const customLogo = $settings.customizer?.sidebar?.business_custom_logo
    if (customLogo && customLogo !== '') return customLogo
    return $settings.business?.full_logo_url || ''
})

const backgroundTheme = computed(() => {
    if (!$settings.customizer?.sidebar?.show_background) return ''

    const { background_type, background_image, background_color } = $settings.customizer.sidebar

    if (background_type === 'image' && background_image) {
        return `background-image: url(${background_image});`
    }
    if (background_type === 'color' && background_color) {
        return `background-color: ${background_color};`
    }
    return ''
})

const sidebarPosition = computed(() => {
    const position = $settings.customizer?.sidebar?.content_position || 'Left top'
    switch (position) {
        case 'Left bottom': return 'justify-end'
        case 'Right bottom': return 'justify-end items-end'
        case 'Right top': return 'items-end'
        default: return ''
    }
})

const isPositionRight = computed(() => {
    return $settings.customizer?.sidebar?.content_position?.includes('Right') || false
})

const findItemPriority = (id) => {
    try {
        if (!$settings?.customizer?.[id]?.priority) return 0
        const priority = Number($settings.customizer[id].priority)
        return isNaN(priority) ? 0 : priority
    } catch (error) {
        console.error('Priority calculation error:', error)
        return 0
    }
}

const menuItems = computed(() => {
    const list = [
        {
            condition: $appointments.selectedService?.service_id,
            link: 'service',
            icon: 'star04',
            title: $appointments.selectedService?.name || '',
            priority: findItemPriority('service')
        },
        {
            condition: $appointments.selectedStaff?.staff_id && $settings.addons?.staffs,
            link: 'staff',
            icon: 'cube01',
            title: $appointments.selectedStaff?.name || '',
            priority: findItemPriority('staff')
        },
        {
            condition: $appointments.selectedDate?.date,
            link: 'date',
            icon: 'calendar',
            title: $appointments.selectedDate ?
                `${$appointments.selectedDate.date} ${$appointments.selectedDate.month?.slice(0, 3)} ${$appointments.selectedDate.year}` :
                '',
            priority: findItemPriority('date')
        },
        {
            condition: $appointments.selectedTime?.booking_time_start && $appointments.selectedTime?.booking_time_end,
            link: 'time',
            icon: 'clock',
            title: $appointments.selectedTime ?
                `${formatTime($appointments.selectedTime.booking_time_start, 'HH:mm')} - ${formatTime($appointments.selectedTime.booking_time_end, 'HH:mm')}` :
                '',
            priority: findItemPriority('time')
        }
    ]

    return list.sort((a, b) => a.priority - b.priority)
})

</script>
<template>
    <div class="sidenav-wrapper" :class="[
        sidebarPosition,
        {
            'rounded-l-lg border-l border-r-0': $customizer.isRounded && !$settings.isRTL,
            'rounded-r-lg border-l-0 border-r': $customizer.isRounded && $settings.isRTL,
            '!border-none': $settings.customizer?.sidebar?.show_background
        }
    ]" :style="backgroundTheme">
        <div class="company-logo-group">
            <img v-if="logo" :src="logo" class="custom-logo" @error="$event.target.style.display = 'none'" />
            <img v-if="!logo && Image" :src="Image('prebook-logo-light.svg')" class="default-logo"
                @error="$event.target.style.display = 'none'" />
        </div>
        <div v-if="$settings.addons?.staffs">
            <div class="company-name" :style="{ color: $settings.customizer?.sidebar?.business_name_color }">
                {{ $settings.customizer?.sidebar?.business_custom_name || $settings.business?.name }}
            </div>
            <div class="company-details" :style="{ color: $settings.customizer?.sidebar?.business_about_color }">
                {{ truncatedAbout }}
                <span v-if="($settings.customizer?.sidebar?.business_custom_about || $settings.business?.name || '').length > 76 || ($settings.customizer?.sidebar?.business_custom_about || $settings.business?.name || '').split('\n').length > 3"
                    class="more-less" @click="showMore = !showMore">
                    {{ showMore ? 'show less' : 'show more' }}
                </span>
            </div>
        </div>
        <div v-else>
            <div class="company-name" :style="{ color: $settings.customizer?.sidebar?.provider_display_name_color }">
                {{ $settings.customizer?.sidebar?.provider_display_name || '' }}
            </div>
            <div class="company-details" :style="{ color: $settings.customizer?.sidebar?.provider_bio_color }">
                {{ truncatedAbout }}
                <span v-if="($settings.customizer?.sidebar?.provider_bio || '').length > 76 || ($settings.customizer?.sidebar?.provider_bio || '').split('\n').length > 3"
                    class="more-less" @click="showMore = !showMore">
                    {{ showMore ? 'show less' : 'show more' }}
                </span>
            </div>
        </div>
        <div class="menu-items-wrapper" :class="{ 'justify-end': isPositionRight }">
            <template v-for="(item, index) in menuItems" :key="index">
                <div v-if="item.condition" @click="$appointments.activeStep = item.link"
                    class="nav-item cursor-pointer"
                    :class="[
                        $settings.customizer.sidebar.show_background ? 'bg-white' : 'bg-light',
                        isPositionRight ? 'position-right' : ''
                    ]"
                    :title="item.title">
                    <Icon size="12px" :name="item.icon" class="icon" />
                    <div class="title">{{ item.title }}</div>
                </div>
            </template>
        </div>
    </div>
</template>